import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withHooks, withMethods, withState } from '@ngrx/signals';
import { catchError, EMPTY, switchMap, timeout } from 'rxjs';
import { BookingDataService } from 'src/shared/services/booking.data.service/booking.data.service';
import { environment } from 'src/environments/environment';
import { RemoteConfigService } from 'src/shared/services/remote-config/remote-config.service';

/**
 * Signal store feature that provides a reset method that is called when the user logs out.
 * If an override method is not provided it will reset to the inputted state.
 */
export function withChatService() {
  const initialChatServiceDocId =
    environment.app === 'trials' ? (null as string | null) : environment.chatServiceDocId || (null as string | null);

  return signalStoreFeature(
    withState({
      chatServiceDocId: initialChatServiceDocId,
      chatService: null as any,
      chatServiceError: null as string | null | undefined | unknown,
    }),
    withMethods(
      (store, bookingDataService = inject(BookingDataService), remoteConfig = inject(RemoteConfigService)) => ({
        setChatService() {
          // Trials: chat service id comes from the screening trial (trial_chat_service_doc_id), not remote config.
          if (environment.app === 'trials') {
            return EMPTY;
          }
          return remoteConfig.$configurations.pipe(
            timeout(5000), // 5 second timeout for tests
            catchError(() => {
              // In test environments or when remote config fails, return empty observable
              console.warn('Remote config not available, skipping chat service initialization');
              patchState(store, { chatService: null, chatServiceError: 'Remote config not available' });
              return EMPTY;
            }),
            switchMap((config) => {
              if (!config) throw new Error('Remote config is not available');
              const chatServiceConfig = config.get('chat_service_doc_id');
              if (!chatServiceConfig) {
                console.warn('chat_service_doc_id not found in remote config');
                patchState(store, { chatService: null, chatServiceError: 'Chat service config not found' });
                return EMPTY;
              }
              const chatServices = JSON.parse(chatServiceConfig);
              patchState(store, { chatServiceDocId: chatServices.vet });
              return bookingDataService.GetChatService(store.chatServiceDocId() || '').pipe(
                tapResponse({
                  next: (chatService) => {
                    patchState(store, { chatService });
                  },
                  error: (error: any) => {
                    console.error('Error fetching chat service:', error);
                    patchState(store, { chatService: null, chatServiceError: error.message || error });
                  },
                })
              );
            })
          );
        },
      })
    ),
    withHooks({
      async onInit(store) {
        if (environment.app === 'trials') {
          return;
        }
        try {
          const observable = store.setChatService();
          // Check if observable will emit any values to avoid EmptyError
          const subscription = observable.subscribe({
            complete: () => {
              // Observable completed without error (likely EMPTY observable)
              console.debug('Chat service initialization completed (no values emitted)');
            },
            error: (error) => {
              console.error('Error initializing chat service:', error);
            },
          });

          // Clean up subscription after a short delay to allow completion
          setTimeout(() => subscription.unsubscribe(), 100);
        } catch (error) {
          console.error('Error initializing chat service:', error);
        }
      },
    })
  );
}
